import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Card, CardHeader } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import type { Permission, Role } from "../../types";

export default function RoleManagement() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    Promise.all([api.get<Role[]>("/api/roles"), api.get<Permission[]>("/api/roles/permissions")])
      .then(([r, p]) => { setRoles(r); setPermissions(p); })
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function selectRole(role: Role) {
    const detail = await api.get<Role>(`/api/roles/${role.id}`);
    setSelectedRole(role);
    setSelectedKeys(new Set(detail.permissions ?? []));
  }

  function toggleKey(key: string) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  async function handleSave() {
    if (!selectedRole) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/roles/${selectedRole.id}/permissions`, { permissionKeys: [...selectedKeys] });
      setSelectedRole(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateRole(e: FormEvent) {
    e.preventDefault();
    try {
      await api.post("/api/roles", { name: newRoleName });
      setCreating(false);
      setNewRoleName("");
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Erstellen fehlgeschlagen.");
    }
  }

  async function handleDeleteRole(role: Role) {
    if (!confirm(`Rolle "${role.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/roles/${role.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  const grouped = permissions.reduce<Record<string, Permission[]>>((acc, p) => {
    (acc[p.category] ??= []).push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-100">Rollen &amp; Rechte</h1>
        <button className="btn-primary" onClick={() => setCreating(true)}>+ Neue Rolle</button>
      </div>

      <Card padded={false}>
        {loading ? <Spinner /> : (
          <table className="table-base">
            <thead><tr><th>Rolle</th><th>Beschreibung</th><th>Typ</th><th /></tr></thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id}>
                  <td className="font-medium text-gray-100">{r.name}</td>
                  <td className="text-gray-400">{r.description ?? "—"}</td>
                  <td>{r.is_system ? <Badge tone="accent">System</Badge> : <Badge tone="gray">Individuell</Badge>}</td>
                  <td className="text-right whitespace-nowrap space-x-3">
                    <button className="text-accent text-xs hover:underline" onClick={() => setEditingRole(r)}>Bearbeiten</button>
                    <button className="text-accent text-xs hover:underline" onClick={() => selectRole(r)}>Rechte bearbeiten</button>
                    {!r.is_system && <button className="text-danger text-xs hover:underline" onClick={() => handleDeleteRole(r)}>Löschen</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {selectedRole && (
        <Modal
          title={`Berechtigungen: ${selectedRole.name}`}
          onClose={() => setSelectedRole(null)}
          size="lg"
          footer={
            <>
              <button className="btn-secondary" onClick={() => setSelectedRole(null)}>Abbrechen</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>Speichern</button>
            </>
          }
        >
          {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2 mb-3">{error}</div>}
          <div className="space-y-5">
            {Object.entries(grouped).map(([category, perms]) => (
              <div key={category}>
                <h4 className="text-sm font-semibold text-gray-300 mb-2">{category}</h4>
                <div className="grid sm:grid-cols-2 gap-2">
                  {perms.map((p) => (
                    <label key={p.key} className="flex items-center gap-2 text-sm text-gray-300 px-3 py-2 rounded-lg bg-surface-raised border border-surface-border cursor-pointer">
                      <input type="checkbox" checked={selectedKeys.has(p.key)} onChange={() => toggleKey(p.key)} />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {creating && (
        <Modal
          title="Neue Rolle"
          onClose={() => setCreating(false)}
          footer={
            <>
              <button className="btn-secondary" onClick={() => setCreating(false)}>Abbrechen</button>
              <button className="btn-primary" form="new-role-form">Erstellen</button>
            </>
          }
        >
          <form id="new-role-form" onSubmit={handleCreateRole}>
            <label className="label">Rollenname *</label>
            <input className="input" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} required />
          </form>
        </Modal>
      )}

      {editingRole && (
        <RoleEditModal
          role={editingRole}
          onClose={() => setEditingRole(null)}
          onSaved={() => { setEditingRole(null); load(); }}
        />
      )}
    </div>
  );
}

/**
 * Bearbeitet Rollenname und Beschreibung einer bestehenden Rolle - getrennt
 * von der Berechtigungsverwaltung ("Rechte bearbeiten"), die davon
 * unberührt bleibt. Der Rollenname wird exakt wie eingegeben übernommen
 * (keine automatische Groß-/Kleinschreibungs-Umwandlung).
 */
function RoleEditModal({ role, onClose, onSaved }: { role: Role; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.put(`/api/roles/${role.id}`, { name, description });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Rolle bearbeiten: ${role.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" form="role-edit-form" disabled={submitting}>{submitting ? "Speichern…" : "Speichern"}</button>
        </>
      }
    >
      <form id="role-edit-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Rollenname *</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={64} />
        </div>
        <div>
          <label className="label">Beschreibung</label>
          <textarea className="input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </form>
    </Modal>
  );
}
